import React, { forwardRef } from 'react'
import Link from 'components/ui/Link/Link'
import { useCallback } from 'sb-hooks'

import type { State } from './MenuContext'

import MenuItem from './MenuItem'


type MenuLinkProps = {
  children?: React.ReactNode
  className?: string
  _ref: (node: React.ReactNode) => void
  index: number
  style?: { [key: string]: any }
  customElement?: React.ReactNode
  component?: React.FunctionComponent<any>
  containerClassName?: string
  state: State
  setState: (state: Partial<State>) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  onClick: (event: React.MouseEvent) => void
  dataTestId?: string

  // ...rest props
  id?: string
  title?: string | ReactIntlMessage
  brand?: string
  icon?: string
  emojiIcon?: string
  to?: string
  toOLD?: string
  redirect?: string
  bold?: boolean
  customColor?: boolean
}

const MenuLink = forwardRef<any, MenuLinkProps>((props, ref) => {
  const {
    _ref, index, style,
    children, customElement, component: Component = Link,
    containerClassName,
    state, setState,
    onKeyDown, onClick,
    dataTestId,
    ...rest
  } = props

  const setRef = useCallback((node) => {
    _ref(node)

    if (ref) {
      // @ts-ignore
      ref(node)
    }
  }, [ _ref ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = useCallback((event) => {
    if (typeof onClick === 'function') {
      onClick(event)
    }

    setState({
      isOpen: false,
      selectionIndex: -1,
      closingWithClick: false,
    })
  }, [ onClick ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = useCallback((event) => {
    if (typeof onKeyDown === 'function') {
      onKeyDown(event)
    }

    if (event.key === 'Enter') {
      // prevent MenuItem's preventDefault from firing,
      // allowing this link to work w/ the keyboard
      event.stopPropagation()
    }
  }, [ onKeyDown ])

  return (
    <MenuItem
      className={containerClassName}
      index={index}
      role="none"
      state={state}
      setState={setState}
    >
      {
        customElement || (
          <Component
            _ref={setRef}
            role="menuitem"
            tabIndex={-1}
            data-selected={index === state.selectionIndex ? true : undefined}
            dataTestId={dataTestId}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            {...rest}
          >
            {children}
          </Component>
        )
      }
    </MenuItem>
  )
})


export default MenuLink
